import { Button, Col, Form, Row } from "antd";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaUserCircle,
  FaPhoneAlt,
  FaCogs,
  FaServer,
  FaFileMedicalAlt,
  FaSitemap,
} from "react-icons/fa";
import ClientName from "./ClientName";
import ClientShortName from "./ClientShortName";
import ClientNamePrefix from "./ClientNamePrefix";
import ClientType from "./ClientType";
import ClientMobile from "./ClientMobile";
import ClientEmail from "./ClientEmail";
import ShowPopulationCount from "./ShowPopulationCount";
import SyncTime from "./SyncTime";
import SessionTime from "./SessionTime";
import TimeZone from "./TimeZone";
import ClientServer from "./ClientServer";
import CovidRisk from "./CovidRisk";
import AdditionalReports from "./AdditionalReports";
import {
  AppDispatch,
  createClientOrganization,
  createClientOrganizationClear,
  createClientOrganizationRequest,
  dynamicClear,
  dynamicSet,
  getClientOrganization,
  getClientOrganizationClear,
  getClientOrganizationRequest,
  Level,
  updateClientOrganization,
  updateClientOrganizationClear,
  updateClientOrganizationRequest,
  UpdateLevelInput,
  useDynamicSelector } from
"@src/redux";
import { useDispatch, useSelector } from "react-redux";
import ClientServerToken from "./ClientServerToken";
import ClientServerUrl from "./ClientServerUrl";
import AllowedMaximumReportUploads from "./AllowedMaximumReportUploads";
import AllowedMaximumReportUploadsName from "./AllowedMaximumReportUploadsName";
import ClientLevel from "./level/ClientLevel";
import SubjectKeyList from "./subject-key/SubjectKey";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import QuestionComponent from "./custom-test/QuestionComponent";
import { capShortQuestion } from "@src/helpers/report";
import { getUuid, parsePrismaError } from "@src/helpers/functions";
import toast from "react-hot-toast";
import AllowReadingManualEntry from "./AllowReadingManualEntry";
import NCD from "./ClientHasNcd";
import DomainName from "./ClientDomainName";

const ClientForm: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm();
  const selectedType = Form.useWatch("type", form);
  const clientServer = Form.useWatch("shouldForwardToOrganizationServer", form);
  const additionalReports = Form.useWatch("hasAdditionalReports", form);
  const navigate = useNavigate();
  const [subject_key, setSubjectKey] = useState<any[]>([]);
  const [question, setQuestions] = useState<any[]>([]);
  const [levels, setLevels] = useState<any[]>([]);
  const [mobile, setMobile] = useState<Object>({});
  const { id } = useParams();

  const { state } = useLocation();
  const {
    result: createClientResult,
    loading: createClientLoading,
    error: createClientError
  } = useSelector(createClientOrganization);
  const {
    result: updateClientResult,
    loading: updateClientLoading,
    error: updateClientError
  } = useSelector(updateClientOrganization);

  const {
    result: getClientOrganizationResult,
    loading: getClientOrganizationLoading
  } = useSelector(getClientOrganization);
  const { removableCustomTestGroupIdList } = useDynamicSelector(
    "removable_custom_test_group_id_list"
  );
  const { removableSubjectKeyIdList } = useDynamicSelector(
    "removable_subject_key_id_list"
  );

  const createOrganizationClient = (values: any) => {
    dispatch(
      createClientOrganizationRequest({
        data: {
          ...values
        }
      })
    );
  };

  const updateOrganizationClientRequest = (values: any) => {
    delete values.usernamePrefix;
    delete values.type;

    dispatch(
      updateClientOrganizationRequest({
        id: id || "",
        data: {
          ...values
        }
      })
    );
  };
  const handleChangeNumber = (values: {
    number: string;
    country_code: string;
    country_short_code: string;
  }) => {
    setMobile(values);
  };

  const handleAddClient = (values: any) => {
    values.maximumReportUploads = values?.maximumReportUploads ?
    Number(values?.maximumReportUploads) :
    undefined;
    values.mobile = mobile;

    // if (levels?.length === 0) {
    //   toast.error("Atleast one level is mandatory");
    // }

    const emptyNameIndex = levels.findIndex((lvl) => !lvl || !lvl.name?.trim());

    if (emptyNameIndex !== -1) {
      const msg =
      emptyNameIndex === 0 ?
      "Atleast one level is mandatory" :
      `Level ${emptyNameIndex + 1} name is mandatory`;

      toast.error(msg);
      return;
    }

    const hasOperatorLogin = levels.some((lvl) => lvl?.allowOperatorLogin);
    if (!hasOperatorLogin) {
      toast.error("At least one level must have Allow Operator enabled");
      return;
    }

    // ✅ subject keys
    delete values.subjectKeys;
    values.subjectKeyList = subject_key.map((subject, index: number) => {
      return {
        name: subject?.name,
        id: subject.id || getUuid(),
        isMandatory: subject?.isMandatory,
        priorityOrder: index + 1
      };
    });

    const emptySubjectKeyIndex = values.subjectKeyList.findIndex(
      (subj) => !subj.name?.trim()
    );
    if (emptySubjectKeyIndex !== -1) {
      toast.error(`Subject Key ${emptySubjectKeyIndex + 1} name is mandatory`);
      return;
    }

    const rootLevelList = Array.isArray(
      getClientOrganizationResult?.rootLevelList
    ) ?
    getClientOrganizationResult.rootLevelList.map((list) => list.id) :
    [];

    const levelIdList = levels.map((lev) => lev.id);

    values.removableLevelIdList = rootLevelList.filter(
      (list) => !levelIdList?.includes(list)
    );

    values.levelList = levels.map((level: UpdateLevelInput, index: number) => {
      let findLevelAttribute =
      getClientOrganizationResult?.rootLevelList?.find(
        (getLevel: Level) => getLevel.id?.includes(level?.id || "")
      )?.levelAttributeList;

      if (index === 0) {
        return {
          id: level.id,
          name: level.name,
          levelAttributeList: level.levelAttributeList,
          // Send null explicitly. Level.upsert leaves absent fields untouched,
          // so omitting this let the first level keep a stale parent and the
          // chain closed into a cycle with no root level.
          parentLevelId: null,
          allowKitAssign: level.allowKitAssign,
          allowOperatorLogin: level.allowOperatorLogin,
          removableLevelAttributeIdList: id ?
          level.removableLevelAttributeIdList :
          undefined,
          orderNumber: index + 1
        };
      } else if (level?.name) {
        return {
          id: level.id,
          name: level.name,
          levelAttributeList: level.levelAttributeList,
          parentLevelId: levels[index - 1].id,
          allowKitAssign: level.allowKitAssign,
          allowOperatorLogin: level.allowOperatorLogin,
          removableLevelAttributeIdList: id ?
          level.removableLevelAttributeIdList :
          undefined,
          orderNumber: index + 1
        };
      }
    });

    // ✅ build customTestGroupList
    values.customTestGroupList = question.map(
      (getQuestion: any, parIndex: number) => {
        const reAssign = getQuestion.customTestList.map(
          (list: any, index: number) => {
            return {
              orderNumber: index + 1,
              name: list?.name,
              // Re-capped on submit: the input already limits typing, but a
              // pasted or hydrated value must not slip past the 30-character
              // rule the report headers rely on.
              shortQuestion: capShortQuestion(list?.shortQuestion),
              id: list?.id,
              type: list?.type,
              optionList: list?.optionList
            };
          }
        );

        return {
          customTestList: reAssign,
          orderNumber: parIndex + 1,
          name: getQuestion?.name,
          id: getQuestion?.id
        };
      }
    );

    // ✅ validate customTestGroupList
    for (
    let gIndex = 0;
    gIndex < values.customTestGroupList.length;
    gIndex++)
    {
      const group = values.customTestGroupList[gIndex];

      // check group name
      if (!group?.name?.trim()) {
        toast.error(`Other Test Group ${gIndex + 1} name is mandatory`);
        return;
      }

      // check inside customTestList
      for (let tIndex = 0; tIndex < group.customTestList.length; tIndex++) {
        const test = group.customTestList[tIndex];

        if (!test?.name?.trim()) {
          toast.error(`Other Test ${tIndex + 1} in Group name is mandatory`);
          return;
        }

        if (!test?.type?.trim()) {
          toast.error(`Other Test ${tIndex + 1} in Group type is mandatory`);
          return;
        }
      }
    }

    const existingTestIds =
    getClientOrganizationResult?.customTestGroupList?.flatMap(
      (group: any) =>
      group?.customTestList?.map((test: any) => test.id) || []
    ) || [];

    const currentTestIds = question.flatMap((q: any) =>
    q?.customTestList?.map((test: any) => test.id)
    );

    values.removableCustomTestIdList = existingTestIds.filter(
      (id: string) => !currentTestIds.includes(id)
    );

    values.removableCustomTestGroupIdList = id ?
    removableCustomTestGroupIdList :
    undefined;
    values.removableSubjectKeyIdList = id ?
    removableSubjectKeyIdList :
    undefined;

    if (!id) {
      delete values.removableLevelIdList;
      delete values.removableCustomTestIdList;
    }

    if (id) updateOrganizationClientRequest(values);else
    createOrganizationClient(values);
  };

  useEffect(() => {
    if (createClientResult?.status === "success") {
      toast.success(t("client_created_successfully"));
      dispatch(createClientOrganizationClear());
      navigate("/settings/clients");
    } else if (updateClientResult?.status === "success") {
      toast.success(t("client_updated_successfully"));
      navigate("/settings/clients");
      dispatch(updateClientOrganizationClear());
    } else if (createClientError?.message) {
      if (createClientError?.message?.includes("Unique")) {
        toast.error("Username already exit in this organization");
      } else if (createClientError?.message?.includes("prisma")) {
        toast.error(t("something_went_wrong"));
      } else {
        toast.error(createClientError?.message);
      }
      dispatch(createClientOrganizationClear());
    } else if (updateClientResult?.error?.message) {
      if (updateClientResult?.error?.message?.includes("Unique")) {
        toast.error("Username already exit in this organization");
      } else if (updateClientResult?.error?.message?.includes("prisma")) {
        toast.error(t("something_went_wrong"));
      } else {
        toast.error(updateClientResult?.error?.message);
      }
      dispatch(updateClientOrganizationClear());
    }
  }, [
  createClientResult,
  createClientError,
  updateClientResult,
  updateClientError]
  );

  useEffect(() => {
    if (id) {
      dispatch(getClientOrganizationRequest({ filter: { id: id } }));
    } else {
      dispatch(getClientOrganizationClear());
      dispatch(dynamicClear("data_source"));
    }
  }, [id]);

  useEffect(() => {
    dispatch(dynamicClear("removable_custom_test_group_id_list"));
    dispatch(dynamicClear("removable_subject_key_id_list"));
  }, []);

  useEffect(() => {
    if (getClientOrganizationResult?.id) {
      form.setFieldsValue({
        ...getClientOrganizationResult,
        maximumReportUploads: Number(
          getClientOrganizationResult?.maximumReportUploads
        ),
        mobile:
        String(getClientOrganizationResult?.mobile?.country_short_code) +
        String(getClientOrganizationResult?.mobile?.number)
      });
      setMobile(getClientOrganizationResult?.mobile || {});
      let reAlternateInitialValues =
      getClientOrganizationResult.customTestGroupList?.map(
        (group: any, index: number) => {
          return {
            ...group,
            key: index.toString(),
            customTestList: group?.customTestList.map(
              (subList: any, index: number) => {
                return {
                  ...subList,
                  key: index.toString()
                };
              }
            )
          };
        }
      );
      dispatch(dynamicSet("data_source", reAlternateInitialValues || []));
    } else if (state) {
      form.setFieldsValue({ type: state.type });
    }
  }, [getClientOrganizationResult, state]);

  const handleBack = () => {
    navigate("/settings/clients");
  };

  if (id && getClientOrganizationLoading && !getClientOrganizationResult?.id) {
    return (
      <div className="client-form-loading">
        <div className="client-form-loading-spinner" />
        <div className="client-form-loading-text">
          {t("loading_client_details") || "Loading client details..."}
        </div>
      </div>);

  }

  return (
    <Form
      layout="vertical"
      className="client-form client-form-modern"
      onFinish={handleAddClient}
      form={form}>

      <div className="client-form-section client-form-section--basic">
        <div className="client-form-section-header">
          <span className="client-form-section-icon">
            <FaUserCircle />
          </span>
          <div>
            <h3 className="client-form-section-title">
              {t("basic_information") || "Basic Information"}
            </h3>
            <div className="client-form-section-subtitle">
              {t("client_basic_subtitle") ||
              "Identify the client with a name, prefix, and type"}
            </div>
          </div>
        </div>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <ClientName />
          </Col>
          <Col xs={24} md={12}>
            <ClientShortName />
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <ClientNamePrefix />
          </Col>
          <Col xs={24} md={12}>
            <ClientType form={form} />
          </Col>
        </Row>
      </div>

      <div className="client-form-section client-form-section--contact">
        <div className="client-form-section-header">
          <span className="client-form-section-icon">
            <FaPhoneAlt />
          </span>
          <div>
            <h3 className="client-form-section-title">
              {t("contact_details") || "Contact Details"}
            </h3>
            <div className="client-form-section-subtitle">
              {t("client_contact_subtitle") ||
              "How can we reach this client?"}
            </div>
          </div>
        </div>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <ClientMobile visible={true} onChange={handleChangeNumber} />
          </Col>
          <Col xs={24} md={12}>
            <ClientEmail />
          </Col>
        </Row>
        <Row gutter={16} align="middle">
          <Col xs={12} md={6}>
            <ShowPopulationCount />
          </Col>
          {selectedType === "screening" &&
          <Col xs={12} md={6}>
              <AllowReadingManualEntry />
            </Col>}

        </Row>
      </div>

      {selectedType && !selectedType?.includes("channel_partner") &&
      <>
          <div className="client-form-section client-form-section--settings">
            <div className="client-form-section-header">
              <span className="client-form-section-icon">
                <FaCogs />
              </span>
              <div>
                <h3 className="client-form-section-title">
                  {t("operational_settings") || "Operational Settings"}
                </h3>
                <div className="client-form-section-subtitle">
                  {t("client_settings_subtitle") ||
                  "Sync, session, timezone and domain configuration"}
                </div>
              </div>
            </div>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <SyncTime />
              </Col>
              <Col xs={24} md={12}>
                <SessionTime />
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <TimeZone />
              </Col>
              <Col xs={24} md={12}>
                <ClientServer />
              </Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <NCD />
              </Col>
              <Col xs={24} md={12}>
                <DomainName />
              </Col>
            </Row>
          </div>

          {clientServer &&
        <div className="client-form-section client-form-section--server">
              <div className="client-form-section-header">
                <span className="client-form-section-icon">
                  <FaServer />
                </span>
                <div>
                  <h3 className="client-form-section-title">
                    {t("client_server_config") || "Client Server"}
                  </h3>
                  <div className="client-form-section-subtitle">
                    {t("client_server_subtitle") ||
                "Forwarding endpoint & authorization token"}
                  </div>
                </div>
              </div>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <ClientServerToken />
                </Col>
                <Col xs={24} md={12}>
                  <ClientServerUrl />
                </Col>
              </Row>
            </div>}


          <div className="client-form-section client-form-section--reports">
            <div className="client-form-section-header">
              <span className="client-form-section-icon">
                <FaFileMedicalAlt />
              </span>
              <div>
                <h3 className="client-form-section-title">
                  {t("risk_and_reports") || "Risk & Reports"}
                </h3>
                <div className="client-form-section-subtitle">
                  {t("client_reports_subtitle") ||
                  "Covid risk and additional report uploads"}
                </div>
              </div>
            </div>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <CovidRisk />
              </Col>
              <Col xs={24} md={12}>
                <AdditionalReports />
              </Col>
            </Row>
            {additionalReports &&
          <Row gutter={16}>
                <Col xs={24} md={12}>
                  <AllowedMaximumReportUploadsName />
                </Col>
                <Col xs={24} md={12}>
                  <AllowedMaximumReportUploads />
                </Col>
              </Row>}

          </div>
        </>}


      <div className="client-form-section client-form-section--levels">
        <div className="client-form-section-header">
          <span className="client-form-section-icon">
            <FaSitemap />
          </span>
          <div>
            <h3 className="client-form-section-title">
              {t("levels_and_tests") || "Levels, Subject Keys & Tests"}
            </h3>
            <div className="client-form-section-subtitle">
              {t("client_levels_subtitle") ||
              "Organizational hierarchy and screening configuration"}
            </div>
          </div>
        </div>
        <div className="client-form-list-container">
          {selectedType && !selectedType?.includes("channel_partner") &&
          <ClientLevel form={form} setLevels={setLevels} />}

          <SubjectKeyList form={form} setSubjectKey={setSubjectKey} />
          <QuestionComponent form={form} setQuestion={setQuestions} />
        </div>
      </div>

      <div className="client-form-actions">
        <Button type="default" onClick={handleBack}>
          {t("back")}
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={createClientLoading || updateClientLoading}>

          {!id ? t("submit") : t("update")}
        </Button>
      </div>
    </Form>);

};

export default ClientForm;